/**
 * Who may do what to a live 5.0 alert — the one rule set behind the desktop Alerts
 * table (CloudEmergencyTable), the mobile alert card (MobileAlertCard, also used by
 * the 5.0 demo panel) and CloudAlertsPage's handler guards.
 *
 * These used to be hand-copied into each view and had drifted: desktop let anyone
 * finish an alert that never required acceptance, mobile let only the accepter.
 * Decided 2026-09-29: **only the caregiver who accepted an alert may finish it**,
 * whether or not acceptance was required. Finish is offered on 'Arrived' (accepted
 * and cleared — see deriveCloudAlarmStatus in AlertNotificationProvider.jsx), so an
 * alert nobody accepted is never finishable from the portal.
 *
 * Works on raw 5.0 alert rows (accepted_user_id, ack_datetime_utc, …), not the
 * normalized shape in activeAlerts.js.
 *
 * Deliberately NOT included, because each caller gates them differently:
 *   - whether the viewer may interact at all (desktop: canInteract / portal_afr;
 *     mobile: whether the handler prop was passed);
 *   - the one-alert-at-a-time block (acceptBlockedReason), which callers show as a
 *     disabled Accept rather than hiding it;
 *   - whether the SafelyYou integration is on, and the View Video role gate.
 */

/**
 * Is this alert accepted by the session user? Prefers accepted_user_id and falls
 * back to accepted_username so a row written before that id existed still resolves.
 * A missing value on either side means "not me", so it fails closed.
 */
export function isAcceptedBy(row, sessionUserId, sessionUsername) {
  return Boolean(
    (row?.accepted_user_id != null && sessionUserId != null
      && String(row.accepted_user_id) === String(sessionUserId))
    || (row?.accepted_username && sessionUsername
      && String(row.accepted_username) === String(sessionUsername)),
  );
}

/**
 * @param {object} row — a 5.0 cloud alert row
 * @param {{ sessionUserId?: any, sessionUsername?: string }} session
 * @returns {{
 *   accepted: boolean, hasAck: boolean, acceptRequired: boolean, acceptedByMe: boolean,
 *   acceptable: boolean,      // Accept applies (before the one-at-a-time block)
 *   finishable: boolean,      // Finish applies — accepter only
 *   releasable: boolean,      // Release applies — accepter only, before clear
 *   ackCandidate: boolean,    // SafelyYou type-40 alert with a link, pre-clear
 *   acknowledgeable: boolean, // …and this user may acknowledge it
 * }}
 */
export function alertActionState(row, { sessionUserId, sessionUsername } = {}) {
  const accepted = Boolean(row?.accepted_display) || Boolean(row?.accepted_datetime_utc);
  const hasAck = Boolean(row?.ack_datetime_utc); // device cleared
  const acceptRequired = Boolean(row?.accept_required);
  const acceptedByMe = isAcceptedBy(row, sessionUserId, sessionUsername);

  // A cleared-but-unaccepted alert can still be accepted.
  const acceptable = acceptRequired && !accepted
    && (row?.status === 'Waiting' || row?.status === 'Overdue' || hasAck);

  const finishable = Boolean(row?.finish_required) && row?.status === 'Arrived' && acceptedByMe;

  /* Status 'Accepted' already means accepted-and-not-yet-cleared, so hasAck is
     redundant with it today. Tested anyway because that derivation lives in another
     module and Release must never appear on a cleared alert. */
  const releasable = row?.status === 'Accepted' && !hasAck && acceptedByMe;

  const isType40 = (row?.event_type_id ?? '').toString() === '40';
  const ackCandidate = isType40 && Boolean(row?.acknowledge_link) && !hasAck;
  /* Acknowledge is the accepter's call when the alert required acceptance; one that
     never required it has no accepter, so anyone may acknowledge it. (Finish was
     tightened to accepter-only; acknowledge was not part of that decision.) */
  const acknowledgeable = ackCandidate && (acceptRequired ? acceptedByMe : true);

  return {
    accepted, hasAck, acceptRequired, acceptedByMe,
    acceptable, finishable, releasable, ackCandidate, acknowledgeable,
  };
}
